import React, {
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
  useTransition,
} from 'react';
import Modal from '../common/models/Modal';
import {
  IproductResponse,
  IproductResponseList,
} from '@/interfaces/productresponse.interface';
import { PageInfo } from '@/interfaces/common.interface';
import { callApi } from '@/api/callApi';
import { API_ENDPOINT, API_URL_PRODUCT_SERVICE } from '@/api/constant';
import { GetFilterUrl, showToastNotification } from '@/utility/helper';
import ImagePreview from '../common/media/ImagePreview';
import FormField from '../UI/FormField';
import { FolderTree, Package, SearchIcon, X } from 'lucide-react';
import { useDebounce } from '@/hooks/useDebounce';
import {
  ICategoryData,
  ICategoryResponse,
} from '@/interfaces/categoryResponse.interface';
type SearchMode = 'product' | 'category';

interface Props {
  show: boolean;
  setShow: React.Dispatch<SetStateAction<boolean>>;
  categoryId: string;
  onTransfer: () => void;
}

interface Step1Props {
  selectedProducts: IproductResponse[];
  setSelectedProducts: React.Dispatch<SetStateAction<IproductResponse[]>>;
  selectedCategory?: ICategoryData;
  setSelectedCategory?: React.Dispatch<SetStateAction<ICategoryData | null>>;
}

export const TransferProducts: React.FC<Props> = ({
  show,
  setShow,
  categoryId,
  onTransfer,
}) => {
  const [mode, setMode] = useState<SearchMode>('product');
  const [selectedCategory, setSelectedCategory] =
    useState<ICategoryData | null>(null);

  const [isPending, startTransition] = useTransition();
  const [selectedProducts, setSelectedProducts] = useState<IproductResponse[]>(
    [],
  );

  const onClose = () => {
    setShow(false);
    setSelectedProducts([]);
  };

  const handleTransfer = () => {
    if (selectedProducts.length === 0) {
      showToastNotification('info', 'Please select at least one product');
      return;
    }
    startTransition(async () => {
      const productIds = selectedProducts.map((i) => i.id);
      await callApi(
        `${API_URL_PRODUCT_SERVICE}/${API_ENDPOINT.products}/category/bulk`,
        { method: 'PATCH', data: { productIds, categoryId } },
      );
      onClose();
      onTransfer();
    });
  };

  return (
    <Modal
      title={`Add Products ${selectedCategory ? `From ${selectedCategory.name}` : ''}`}
      show={show}
      onClose={onClose}
      size="w-[700px]"
      height="max-h-140"
    >
      <div className="grid grid-cols-2 gap-2 rounded-lg border border-gray-400/30 bg-gray-400/2 p-1 mb-3">
        <button
          type="button"
          onClick={() => {
            setMode('product');
            setSelectedProducts([]);
            setSelectedCategory(null);
          }}
          className={`flex items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-all ${
            mode === 'product'
              ? 'bg-white shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          <Package className="h-3.5 w-3.5" /> Search by product
        </button>
        <button
          type="button"
          onClick={() => {
            setMode('category');
            setSelectedCategory(null);
            setSelectedProducts([]);
            // setSelected(new Set());
          }}
          className={`flex items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-all ${
            mode === 'category'
              ? 'bg-white shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          <FolderTree className="h-3.5 w-3.5" /> Search by category
        </button>
      </div>
      {mode === 'product' && (
        <Step1
          selectedProducts={selectedProducts}
          setSelectedProducts={setSelectedProducts}
        />
      )}
      {mode === 'category' && !selectedCategory && (
        <Step2 setSelectedCategory={setSelectedCategory} />
      )}

      {mode === 'category' && selectedCategory && (
        <Step1
          selectedProducts={selectedProducts}
          setSelectedProducts={setSelectedProducts}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
        />
      )}

      <div className="flex items-center justify-end gap-3">
        <button
          disabled={isPending}
          onClick={onClose}
          className="btn btn-secondary"
        >
          Cancel
        </button>
        <button
          onClick={() => handleTransfer()}
          disabled={selectedProducts.length === 0 || isPending}
          className="btn btn-primary"
        >
          Add Product
        </button>
      </div>
    </Modal>
  );
};

const Step1: React.FC<Step1Props> = ({
  selectedProducts,
  setSelectedProducts,
  setSelectedCategory,
  selectedCategory,
}) => {
  const [products, setProducts] = useState<IproductResponse[]>([]);
  const [search, setSearch] = useState('');
  const debounceValue = useDebounce(search);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [pageInfo, setPageInfo] = useState<PageInfo>({
    page: 1,
    limit: 10,
    total: 1,
    totalPages: 1,
  });

  const handleSelectAll = () => {
    const allSelected = products.every((product) =>
      selectedProducts.some((selected) => selected.id === product.id),
    );

    if (allSelected) {
      // Remove all visible products
      setSelectedProducts((prev) =>
        prev.filter(
          (selected) => !products.some((product) => product.id === selected.id),
        ),
      );
    } else {
      // Add all visible products
      setSelectedProducts((prev) => {
        const existingIds = new Set(prev.map((p) => p.id));

        const newProducts = products.filter(
          (product) => !existingIds.has(product.id),
        );

        return [...prev, ...newProducts];
      });
    }
  };

  const fetchProducts = useCallback(
    async (signal?: AbortSignal) => {
      const isLoadMore = pageInfo.page > 1;

      if (isLoadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      const query = GetFilterUrl({
        page: pageInfo.page,
        limit: pageInfo.limit,
        status: 'active',
        search: debounceValue,
        categoryIds: selectedCategory?.id,
      });

      try {
        const res = await callApi<IproductResponseList>(
          `${API_URL_PRODUCT_SERVICE}/${API_ENDPOINT.products}?${query}`,
          {
            method: 'GET',
            signal,
          },
        );

        setProducts((prev) =>
          pageInfo.page === 1 ? res.data : [...prev, ...res.data],
        );

        setPageInfo(res.pageInfo);
      } catch (error: any) {
        if (error.name !== 'AbortError') {
          console.error(error);
        }
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [pageInfo.page, pageInfo.limit, debounceValue],
  );

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    setProducts([]);
    setPageInfo((prev) => ({
      ...prev,
      page: 1,
    }));
  }, [debounceValue]);

  const handleProductSelection = (product: IproductResponse) => {
    setSelectedProducts((prev) => {
      const exists = prev.some((p) => p.id === product.id);

      if (exists) {
        return prev.filter((p) => p.id !== product.id);
      }

      return [...prev, product];
    });
  };
  const isAllSelected =
    products.length > 0 &&
    products.every((product) =>
      selectedProducts.some((selected) => selected.id === product.id),
    );

  const isSomeSelected =
    products.some((product) =>
      selectedProducts.some((selected) => selected.id === product.id),
    ) && !isAllSelected;

  const selectAllRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = isSomeSelected;
    }
  }, [isSomeSelected]);

  return (
    <div className="overflow-y-scroll h-full">
      <div className="flex items-center px-3 py-2 ">
        <div className="max-w-fit">
          {products.length > 0 || !setSelectedCategory ? (
            <FormField
              type="checkbox"
              className="text-gray-400 px-2"
              aria-label="Select product"
              checked={isAllSelected}
              onChange={handleSelectAll}
            />
          ) : (
            <button
              onClick={() => {
                setSelectedCategory(null);
                setSelectedProducts([]);
              }}
              className="btn btn-secondary px-2! py-1! border-0!"
            >
              <X />
            </button>
          )}
        </div>

        <div className="relative flex-grow mb-1">
          <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400/50">
            <SearchIcon size={20} />
          </span>
          <input
            type="text"
            placeholder="Search products....."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.preventDefault();
            }}
            className="w-full pl-11 pr-4 duration-200 outline-none placeholder-light-40"
          />
        </div>
      </div>
      {loading ? (
        <div>
          {Array.from({ length: 5 }).map((_, index) => (
            <ProductSkeleton key={index} />
          ))}
        </div>
      ) : products.length === 0 ? (
        <EmptyProducts />
      ) : (
        <ul>
          {products.map((item) => {
            const url = item?.directImageUrl
              ? item.directImageUrl
              : item?.media?.[0]?.url;

            return (
              <li
                key={item.id}
                className="flex items-center gap-3 border-t border-gray-400/30 p-3 transition"
              >
                <div className="max-w-fit">
                  <FormField
                    type="checkbox"
                    className="text-gray-400 px-2"
                    aria-label="Select product"
                    checked={selectedProducts.some(
                      (product) => product.id === item.id,
                    )}
                    onChange={() => handleProductSelection(item)}
                  />
                </div>

                <div className="flex-1">
                  <ImagePreview
                    src={`${API_URL_PRODUCT_SERVICE}/${url}`}
                    text={item.name}
                    textClass="w-[300px]"
                  />
                </div>
              </li>
            );
          })}

          <div className="flex justify-center py-4">
            {pageInfo.totalPages > pageInfo.page && (
              <button
                className="btn-secondary min-w-[120px]"
                disabled={loadingMore}
                onClick={() =>
                  setPageInfo((prev) => ({
                    ...prev,
                    page: prev.page + 1,
                  }))
                }
              >
                {loadingMore ? (
                  <span className="flex items-center gap-2 justify-center">
                    <span className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                    Loading
                  </span>
                ) : (
                  'Load more'
                )}
              </button>
            )}
          </div>
        </ul>
      )}
    </div>
  );
};

const Step2 = ({
  setSelectedCategory,
}: {
  setSelectedCategory: React.Dispatch<SetStateAction<ICategoryData | null>>;
}) => {
  const [categories, setCategories] = useState<ICategoryData[]>([]);
  const [search, setSearch] = useState('');
  const debounceValue = useDebounce(search);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [pageInfo, setPageInfo] = useState<PageInfo>({
    page: 1,
    limit: 10,
    total: 1,
    totalPages: 1,
  });

  const fetchCategories = useCallback(
    async (signal?: AbortSignal) => {
      const isLoadMore = pageInfo.page > 1;

      if (isLoadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      const query = GetFilterUrl({
        page: pageInfo.page,
        limit: pageInfo.limit,
        status: 'active',
        search: debounceValue,
      });

      try {
        const res = await callApi<ICategoryResponse>(
          `${API_URL_PRODUCT_SERVICE}/${API_ENDPOINT.categories}?${query}`,
          {
            method: 'GET',
            signal,
          },
        );

        setCategories((prev) =>
          pageInfo.page === 1 ? res.data : [...prev, ...res.data],
        );

        setPageInfo(res.pageInfo);
      } catch (error: any) {
        if (error.name !== 'AbortError') {
          console.error(error);
        }
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [pageInfo.page, pageInfo.limit, debounceValue],
  );

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    setCategories([]);
    setPageInfo((prev) => ({
      ...prev,
      page: 1,
    }));
  }, [debounceValue]);
  return (
    <div className="overflow-y-scroll">
      <div className="relative flex-grow py-2">
        <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400/50">
          <SearchIcon size={20} />
        </span>
        <input
          type="text"
          placeholder="Search categories...."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.preventDefault();
          }}
          className="w-full pl-11 pr-4 duration-200 outline-none placeholder-light-40"
        />
      </div>
      {loading ? (
        <div>
          {Array.from({ length: 5 }).map((_, index) => (
            <ProductSkeleton key={index} />
          ))}
        </div>
      ) : categories.length === 0 ? (
        <EmptyProducts />
      ) : (
        <ul>
          {categories.map((item) => {
            const url = item?.media?.[0]?.url;

            return (
              <li
                onClick={() => setSelectedCategory(item)}
                key={item.id}
                className="flex items-center gap-3 border-t border-gray-400/30 p-3 transition"
              >
                <div className="flex-1">
                  <ImagePreview
                    src={`${API_URL_PRODUCT_SERVICE}/${url}`}
                    text={item.name}
                    textClass="w-[300px]"
                  />
                </div>
              </li>
            );
          })}

          <div className="flex justify-center py-4">
            {pageInfo.totalPages > pageInfo.page && (
              <button
                className="btn-secondary min-w-[120px]"
                disabled={loadingMore}
                onClick={() =>
                  setPageInfo((prev) => ({
                    ...prev,
                    page: prev.page + 1,
                  }))
                }
              >
                {loadingMore ? (
                  <span className="flex items-center gap-2 justify-center">
                    <span className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                    Loading
                  </span>
                ) : (
                  'Load more'
                )}
              </button>
            )}
          </div>
        </ul>
      )}
    </div>
  );
};

const ProductSkeleton = () => {
  return (
    <div className="flex items-center gap-3 border-t border-gray-400/30 p-3 animate-pulse">
      <div className="w-5 h-5 rounded bg-gray-400/40" />

      <div className="flex items-center gap-3 flex-1">
        <div className="w-12 h-12 rounded bg-gray-400/40" />

        <div className="space-y-2">
          <div className="h-4 w-56 bg-gray-400/40 rounded" />
          <div className="h-3 w-32 bg-gray-200 rounded" />
        </div>
      </div>
    </div>
  );
};

const EmptyProducts = () => {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-gray-500">
      <div className="w-14 h-14 rounded-full bg-gray-400/10 flex items-center justify-center mb-3">
        <SearchIcon className="w-7 h-7 text-gray-400" />
      </div>

      <p className="text-sm font-medium">No products available</p>

      <span className="text-xs text-gray-400 mt-1">
        Try changing your search criteria
      </span>
    </div>
  );
};
